import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { PhoneIncoming, X } from "lucide-react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getSupabaseClient } from "../atomic-crm/providers/supabase/supabase";
import { useMyExtension } from "./useMyExtension";
import {
  callerLabel,
  fetchCalls,
  fetchPopContact,
  fetchRecentNotes,
  formatDuration,
  formatPhone,
  phoneEnabled,
  type CallRecord,
  type IncomingCall,
  type PopContact,
  type PopNote,
} from "./phoneApi";

interface Pop {
  call: IncomingCall;
  contact: PopContact | null;
  notes: PopNote[];
  recentCalls: CallRecord[];
}

const DISMISS_AFTER_MS = 20_000;

/**
 * Screen-pop: when a call rings the signed-in user's extension, show who is calling, their last notes and
 * recent calls. Subscribes to `incoming_calls` inserts/updates for that extension over Supabase Realtime.
 */
export function IncomingCallPanel() {
  const { extension } = useMyExtension();
  const [pops, setPops] = useState<Pop[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    if (!phoneEnabled || !extension) return;
    const client = getSupabaseClient();
    const channel: RealtimeChannel = client
      .channel(`incoming-calls-${extension}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "incoming_calls",
          filter: `extension=eq.${extension}`,
        },
        (payload) => {
          void addPop(payload.new as IncomingCall);
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "incoming_calls",
          filter: `extension=eq.${extension}`,
        },
        (payload) => {
          const updated = payload.new as IncomingCall;
          setPops((prev) =>
            prev.map((p) =>
              p.call.id === updated.id ? { ...p, call: updated } : p,
            ),
          );
          if (updated.status !== "ringing") scheduleDismiss(updated.id);
        },
      )
      .subscribe();

    const addPop = async (call: IncomingCall) => {
      const [contact, notes, recentCalls] = await Promise.all([
        call.contact_id
          ? fetchPopContact(call.contact_id)
          : Promise.resolve(null),
        call.contact_id
          ? fetchRecentNotes(call.contact_id)
          : Promise.resolve([]),
        call.contact_id
          ? fetchCalls({ contact_id: call.contact_id }, 3)
          : Promise.resolve([]),
      ]);
      setPops((prev) =>
        [
          { call, contact, notes, recentCalls },
          ...prev.filter((p) => p.call.id !== call.id),
        ].slice(0, 3),
      );
      notifyDesktop(call, contact);
    };

    const scheduleDismiss = (id: number) => {
      const existing = timers.current.get(id);
      if (existing) clearTimeout(existing);
      timers.current.set(
        id,
        setTimeout(
          () => setPops((prev) => prev.filter((p) => p.call.id !== id)),
          DISMISS_AFTER_MS,
        ),
      );
    };

    const timerMap = timers.current;
    return () => {
      void client.removeChannel(channel);
      timerMap.forEach((t) => clearTimeout(t));
      timerMap.clear();
    };
  }, [extension]);

  if (!phoneEnabled || pops.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-3"
      aria-live="polite"
    >
      {pops.map((pop) => (
        <PopCard
          key={pop.call.id}
          pop={pop}
          onClose={() =>
            setPops((prev) => prev.filter((p) => p.call.id !== pop.call.id))
          }
        />
      ))}
    </div>
  );
}

function PopCard({ pop, onClose }: { pop: Pop; onClose: () => void }) {
  const { call, contact, notes, recentCalls } = pop;
  const label = callerLabel(call, contact);
  return (
    <Card className="shadow-lg border-primary/40">
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <PhoneIncoming
            className={`size-4 ${call.status === "ringing" ? "animate-pulse text-primary" : "text-muted-foreground"}`}
          />
          <span>{label}</span>
          <StatusBadge status={call.status} />
        </CardTitle>
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          aria-label="Dismiss"
          onClick={onClose}
        >
          <X className="size-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="text-muted-foreground">
          {formatPhone(call.caller_number)}
          {contact?.companies?.name ? ` · ${contact.companies.name}` : ""}
          {contact?.title ? ` · ${contact.title}` : ""}
        </div>
        {contact ? (
          <>
            {notes.length > 0 && (
              <div>
                <div className="font-medium">Last notes</div>
                <ul className="mt-1 space-y-1">
                  {notes.map((n) => (
                    <li
                      key={n.id}
                      className="line-clamp-3 text-muted-foreground"
                    >
                      <span className="text-xs">
                        {new Date(n.date).toLocaleDateString()}
                      </span>{" "}
                      {n.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {recentCalls.length > 0 && (
              <div>
                <div className="font-medium">Recent calls</div>
                <ul className="mt-1 space-y-1">
                  {recentCalls.map((c) => (
                    <li
                      key={c.id}
                      className="line-clamp-2 text-muted-foreground"
                    >
                      <span className="text-xs">
                        {c.started_at
                          ? new Date(c.started_at).toLocaleDateString()
                          : ""}{" "}
                        · {formatDuration(c.duration_s)}
                      </span>{" "}
                      {c.summary}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Button asChild size="sm">
              <Link to={`/contacts/${contact.id}/show`}>Open contact</Link>
            </Button>
          </>
        ) : (
          <Button asChild size="sm" variant="outline">
            <Link
              to={`/contacts/create?phone=${encodeURIComponent(call.caller_number)}`}
            >
              New contact from this number
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: IncomingCall["status"] }) {
  const text: Record<IncomingCall["status"], string> = {
    ringing: "Ringing",
    answered: "Answered",
    missed: "Missed",
    voicemail: "Voicemail",
    ai: "Answered by AI",
  };
  return (
    <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
      {text[status]}
    </span>
  );
}

function notifyDesktop(call: IncomingCall, contact: PopContact | null) {
  if (
    typeof Notification === "undefined" ||
    Notification.permission !== "granted"
  )
    return;
  try {
    new Notification(`Incoming call: ${callerLabel(call, contact)}`, {
      body: [formatPhone(call.caller_number), contact?.companies?.name]
        .filter(Boolean)
        .join(" · "),
      tag: `incoming-${call.id}`,
    });
  } catch {
    // notifications are a convenience only
  }
}
